import type { ServiceItem } from '@/cms/fields/types/checkout-page'
import { Banner, type BannerColors } from '@sunwebgroup/uds-ui/banner'
import { Benefits } from '@sunwebgroup/uds-ui/benefits'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Dropdown } from '@sunwebgroup/uds-ui/dropdown'
import { SwitchField } from '@sunwebgroup/uds-ui/fields/switch-field'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { isEmpty, values } from 'ramda'
import * as React from 'react'
import { useSessionStorage } from 'react-use'
import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import {
  updateLuggageService,
  updateOneClickLuggageService,
  updateSpecialLuggageService,
} from '@/scopes/checkout/domain/actions'
import { createAncillariesClickedEvent } from '@/scopes/checkout/domain/analytics'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useBookingDraftQuery, useCheckoutSid, useService } from '@/scopes/checkout/domain/hooks'
import { getAirlineNameIfAllAirlinesAreTheSame } from '@/scopes/checkout/domain/policies/airline'
import { bannerColorMap, type CheckoutServiceOption, ServiceType } from '@/scopes/checkout/domain/types'
import { getAirlinesNames } from '@/scopes/checkout/pages/selectors'
import { BannerType } from '@/scopes/common/domain/types/banner'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import genericStyles from '../GenericService/GenericService.module.css'
import serviceStyles from '../ServiceComponents.module.css'
import styles from './LuggageService.module.css'

const maxOneClickOptionsToBeShown = 3

export function LuggageService({ item }: { item: ServiceItem }) {
  const { componentTitle, componentDescription, popupButtonAriaLabel, popupTitle, popupDescription, popupReadMoreLabel, serviceConfigurations } = item

  const intl = useIntl()
  const sid = useCheckoutSid()
  const { sendEvent } = useAnalytics()
  const showSpecialLuggage = serviceConfigurations?.showSpecialLuggage || false

  const { service, setServiceValue } = useService(ServiceType.Luggage)
  useSendAncillaryAvailable(service, 'selectLuggage')

  const [selectPerParticipantChecked, setSelectPerParticipantChecked] = useSessionStorage(
    `${sid}-luggage-is-per-participant`,
    !service?.isOneClickLuggageReady,
  )

  const switchLuggageVisualisation = React.useCallback(
    (value: boolean) => {
      setSelectPerParticipantChecked(value)
      sendEvent(
        createAncillariesClickedEvent({
          description: 'Change luggage visualization',
          element: 'toggle',
          type: 'Luggage',
          result: !selectPerParticipantChecked ? 'element selected' : 'element unselected',
        }),
      )
    },
    [sendEvent, setSelectPerParticipantChecked, selectPerParticipantChecked],
  )

  const handleSpecialLuggage = React.useCallback(
    (value: boolean) => {
      if (!service)
        return
      setServiceValue(updateSpecialLuggageService({ value })(service))
      sendEvent(
        createAncillariesClickedEvent({
          description: 'Set special luggage',
          element: 'toggle',
          type: 'Special Luggage',
          result: value ? 'element selected' : 'element unselected',
        }),
      )
    },
    [sendEvent, setServiceValue, service],
  )

  if (!service)
    return null
  const { hasSpecialLuggage } = service

  return (
    <CheckoutSection
      title={componentTitle || intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_title' })}
      description={componentDescription || intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_description' })}
      glyph={getIconForService(ServiceType.Luggage)}
      moreInformationTitle={popupTitle || intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_modal_title' })}
      moreInformationDescription={popupDescription || intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_modal_description' })}
      moreInformationButtonAriaLabel={popupButtonAriaLabel}
      moreInformationReadMoreLabel={popupReadMoreLabel}
    >
      <div className={styles.flexContainer}>
        {service.isOneClickLuggageReady && (
          <>
            <Typography variant="body-m-bold-snug">{service.flightAirports}</Typography>
            <LuggageServiceOneClick />
            <SwitchField
              label={intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_applies_to_all_participants' })}
              onCheckedChange={switchLuggageVisualisation}
              checked={selectPerParticipantChecked}
              variant="transparent"
            />
          </>
        )}
        {selectPerParticipantChecked && <LuggageServicePerParticipant /> }
        {showSpecialLuggage && (
          <>
            <SwitchField
              label={intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_set_special_luggage' })}
              defaultChecked={hasSpecialLuggage}
              onCheckedChange={handleSpecialLuggage}
              supportingLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_set_special_luggage_extra_text' })}
            />
          </>
        )}
      </div>
    </CheckoutSection>
  )
}

function LuggageServicePerParticipant() {
  const { service } = useService(ServiceType.Luggage)
  const { data } = useBookingDraftQuery()
  const intl = useIntl()
  const airlinesNames = getAirlinesNames(data?.transportInfo)

  if (!service)
    return null
  const { outboundOptions, inboundOptions, rooms, participants, banners } = service
  const uniqueBanners = Array.from(
    new Map(
      banners.map(banner => [banner.title, banner]),
    ).values(),
  )

  return (
    <div className={styles.flexContainer}>
      <FlightsInformation />
      {(values(outboundOptions).length > 0 || values(inboundOptions).length > 0) && (
        rooms.map(({ id: roomId, name: roomName, participantIds }) => (
          <div className={styles.roomParticipantsWrapper} key={roomId}>
            {rooms.length > 1 && (
              <Typography as="p" className={serviceStyles.roomName}>
                {roomName}
              </Typography>
            )}
            {participantIds.map((participantId) => {
              const participant = participants?.[participantId]
              if (!participant || participant.availableOptions.length === 0) {
                return null
              }

              return (
                <ParticipantRow
                  {...participant}
                  key={participantId}
                  outboundOptions={outboundOptions}
                  inboundOptions={inboundOptions}
                />
              )
            })}
          </div>
        ))
      )}
      {(values(outboundOptions).length > 0 || values(inboundOptions).length === 0) && (
        isEmpty(rooms)
        && Object.values(participants ?? {}).map(participant => (
          <ParticipantRow {...participant} key={participant.id} outboundOptions={outboundOptions} inboundOptions={inboundOptions} />
        ))
      )}
      { service.disableInboundSelection && (
        <Banner
          color={'informative' as BannerColors}
          size="M"
        >
          <Banner.TextsWrapper>
            <Banner.Text>
              {intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_inbound_selection_disabled_banner_title' })}
            </Banner.Text>
            <Banner.SupportingText innerHTML={intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_inbound_selection_disabled_banner_text' }, { airlineName: getAirlineNameIfAllAirlinesAreTheSame(airlinesNames) })} />
          </Banner.TextsWrapper>
        </Banner>
      )}
      { uniqueBanners.map(({ title, text, type = 'info' }, index) => (
        <Banner
          key={`banner-${index}`}
          color={bannerColorMap[type as BannerType] as BannerColors}
          size="M"
        >
          <Banner.TextsWrapper>
            {title && (
              <Banner.Text>
                {title}
              </Banner.Text>
            )}
            {text && <Banner.SupportingText innerHTML={text} />}
          </Banner.TextsWrapper>
        </Banner>
      ))}
    </div>
  )
}

function LuggageServiceOneClick() {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { service, setServiceValue } = useService(ServiceType.Luggage)

  const handleOneClickLuggageChange = React.useCallback(
    (value: string | null) => {
      if (!service)
        return
      setServiceValue(updateOneClickLuggageService({ value })(service))

      const optionClicked = service.oneClickLuggageOptions[value ?? '']
      sendEvent(
        createAncillariesClickedEvent({
          description: 'selectOneClickLuggage',
          element: 'button',
          type: optionClicked?.label || '',
          result: optionClicked?.price || 0,
        }),
      )
    },
    [sendEvent, setServiceValue, service],
  )

  if (!service)
    return null

  const { oneClickLuggageOptions, oneClickSelectedValue } = service

  return (
    <div className={styles.oneClickLuggageOptions}>
      {Object.values(oneClickLuggageOptions).slice(0, maxOneClickOptionsToBeShown).map((option) => {
        const { value, label, price, badge } = option

        const isSelected = value === oneClickSelectedValue

        return (
          <div key={value} className={cn(styles.oneClickLuggageOption, isSelected && styles.oneClickLuggageOptionSelected)}>
            <ListItem variant="outlined">
              <div className={cn(genericStyles.optionItemsContainer)}>
                <div className={cn(genericStyles.itemOptionContent)}>
                  {badge && <Tag variant="success-tinted" size="M" className={cn(genericStyles.itemOptionTag)}>{badge}</Tag>}
                  <Typography variant="body-m-bold-snug" className={styles.oneClickLuggageLabel}>{label}</Typography>
                </div>
                <Benefits.Root display="list">
                  <Benefits.Item
                    className={styles.oneClickLuggageBulletListItem}
                    title={intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_one_click_handbagage_inclusief' })}
                  />
                </Benefits.Root>
                <div className={styles.priceDisplayRow}>
                  <Typography variant="body-s-regular-tight" className={styles.oneClickLuggagePriceDetail}>{intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_one_click_price_label' })}</Typography>
                  <Typography variant="body-m-bold-snug">
                    {intl.formatCurrency(price, { showSign: price < 0 })}
                  </Typography>
                </div>
                {isSelected && (
                  <Button variant="transparentNeutral" fullWidth onClick={() => handleOneClickLuggageChange(null)}>
                    <Button.Icon glyph="TrashCan" />
                    {intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_one_click_remove' })}
                  </Button>
                )}
                {!isSelected && (
                  <Button variant="outlinedNeutral" fullWidth onClick={() => handleOneClickLuggageChange(value)}>
                    {intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_one_click_select' })}
                  </Button>
                )}
              </div>
            </ListItem>
          </div>
        )
      })}
    </div>
  )
}

function FlightsInformation() {
  const { service } = useService(ServiceType.Luggage)

  if (!service)
    return null
  const { flightsInformationBanner } = service
  const flightsInformationHtml = flightsInformationBanner && (flightsInformationBanner.outboundFlight || flightsInformationBanner.inboundFlight)
    ? (
        <>
          <div className={styles.dividerLine} />
          <div className={styles.flightsInformationText}>
            {flightsInformationBanner.outboundFlight && (
              <div className={styles.group}>
                <Typography variant="body-m-bold-snug">{flightsInformationBanner.outboundFlight.title}</Typography>
                <ul>
                  <li><Typography variant="body-m-regular-snug">{flightsInformationBanner.outboundFlight.handLuggage}</Typography></li>
                  <li><Typography variant="body-m-regular-snug">{flightsInformationBanner.outboundFlight.checkedInLuggage}</Typography></li>
                </ul>
              </div>
            )}
            {flightsInformationBanner.inboundFlight && (
              <div className={styles.group}>
                <Typography variant="body-m-bold-snug">{flightsInformationBanner.inboundFlight.title}</Typography>
                <ul>
                  <li><Typography variant="body-m-regular-snug">{flightsInformationBanner.inboundFlight.handLuggage}</Typography></li>
                  <li><Typography variant="body-m-regular-snug">{flightsInformationBanner.inboundFlight.checkedInLuggage}</Typography></li>
                </ul>
              </div>
            )}
          </div>
        </>
      )
    : null

  return (
    <>
      {flightsInformationBanner && (
        <Banner
          color={bannerColorMap[flightsInformationBanner.type as BannerType] as BannerColors}
          size="M"
        >
          <Banner.TextsWrapper>
            {flightsInformationBanner.title && <Banner.Text>{flightsInformationBanner.title}</Banner.Text>}
            {flightsInformationBanner.text && <Banner.SupportingText innerHTML={flightsInformationBanner.text} />}
            {flightsInformationHtml}
          </Banner.TextsWrapper>
        </Banner>
      )}
    </>
  )
}

function ParticipantRow({
  id,
  outboundOptions,
  inboundOptions,
  availableOptions,
  selectedValueInbound,
  selectedValueOutbound,
  name,
}: {
  id: string
  outboundOptions: Record<string, CheckoutServiceOption>
  inboundOptions: Record<string, CheckoutServiceOption>
  availableOptions: string[]
  selectedValueInbound: string | null
  selectedValueOutbound: string | null
  name: string
}) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { service, setServiceValue } = useService(ServiceType.Luggage)
  const selectedOutboundOption = selectedValueOutbound ? outboundOptions[selectedValueOutbound] : undefined
  const selectedInboundOption = selectedValueInbound ? inboundOptions[selectedValueInbound] : undefined
  const [totalParticipantAmount, setTotalParticipantAmount] = React.useState((selectedOutboundOption?.price ?? 0) + (selectedInboundOption?.price ?? 0))

  const handleOutboundChange = React.useCallback(
    (value: string) => {
      if (!service)
        return
      setServiceValue(updateLuggageService({ participantId: id, value, direction: 'outbound' })(service))
      setTotalParticipantAmount((outboundOptions[value ?? '']?.price ?? 0) + (inboundOptions[selectedValueInbound ?? '']?.price ?? 0))
      sendEvent(
        createAncillariesClickedEvent({
          description: 'selectLuggage',
          element: 'dropdown',
          type: outboundOptions[value ?? '']?.label ?? '',
          result: outboundOptions[value ?? '']?.price ?? 0,
        }),
      )
    },
    [sendEvent, setServiceValue, service, setTotalParticipantAmount, id, selectedValueInbound, outboundOptions, inboundOptions],
  )

  const handleInboundChange = React.useCallback(
    (value: string) => {
      if (!service)
        return
      setServiceValue(updateLuggageService({ participantId: id, value, direction: 'inbound' })(service))
      setTotalParticipantAmount((outboundOptions[selectedValueOutbound ?? '']?.price ?? 0) + (inboundOptions[value ?? '']?.price ?? 0))
      sendEvent(
        createAncillariesClickedEvent({
          description: 'selectLuggage',
          element: 'dropdown',
          type: inboundOptions[value ?? '']?.label ?? '',
          result: inboundOptions[value ?? '']?.price ?? 0,
        }),
      )
    },
    [sendEvent, setServiceValue, service, setTotalParticipantAmount, id, selectedValueOutbound, outboundOptions, inboundOptions],
  )

  React.useEffect(() => {
    if (selectedValueOutbound) {
      setTotalParticipantAmount((outboundOptions[selectedValueOutbound]?.price ?? 0) + (inboundOptions[selectedValueInbound ?? '']?.price ?? 0))
    }
    if (selectedValueInbound) {
      setTotalParticipantAmount((outboundOptions[selectedValueOutbound ?? '']?.price ?? 0) + (inboundOptions[selectedValueInbound]?.price ?? 0))
    }
  }, [selectedValueOutbound, selectedValueInbound, inboundOptions, outboundOptions])

  if (!availableOptions.length)
    return null
  if (!outboundOptions && !inboundOptions)
    return null
  if (values(outboundOptions).length === 0 && values(inboundOptions).length === 0)
    return null

  return (
    <div className={styles.participantRowWrapper}>
      <Typography className={serviceStyles.participantName}>
        {name}
      </Typography>
      <div className={styles.participantDropdowns}>
        {Object.values(outboundOptions).length > 0 && (
          <div className={styles.participantDropdown} translate="no">
            <Dropdown.Root value={selectedValueOutbound ?? ''} onValueChange={newValue => handleOutboundChange(newValue ?? '')}>
              <Dropdown.Trigger
                placeholder={intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_outbound_placeholder' })}
                label={intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_outbound_label' })}
              />
              <Dropdown.Content>
                {Object.values(outboundOptions).map(({ label, value, price }) => (
                  <Dropdown.Item key={value} value={value} disabled={!availableOptions.includes(value)}>
                    {`${label} | ${intl.formatCurrency(price)}`}
                  </Dropdown.Item>
                ))}
              </Dropdown.Content>
            </Dropdown.Root>
          </div>
        )}
        {Object.values(inboundOptions).length > 0 && (
          <div className={styles.participantDropdown} translate="no">
            <Dropdown.Root value={selectedValueInbound ?? ''} onValueChange={newValue => handleInboundChange(newValue ?? '')} disabled={service?.disableInboundSelection}>
              <Dropdown.Trigger
                placeholder={intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_inbound_placeholder' })}
                label={intl.formatMessage({ id: 'checkout_steps_ancillaries_luggage_inbound_label' })}
                disabled={service?.disableInboundSelection}
              />
              <Dropdown.Content>
                {Object.values(inboundOptions).map(({ label, value, price }) => (
                  <Dropdown.Item key={value} value={value} disabled={!availableOptions.includes(value)}>
                    {`${label} | ${intl.formatCurrency(price)}`}
                  </Dropdown.Item>
                ))}
              </Dropdown.Content>
            </Dropdown.Root>
          </div>
        )}
        <Typography variant="body-m-regular-snug" className={styles.totalParticipantAmount}>
          {intl.formatCurrency(totalParticipantAmount)}
        </Typography>
      </div>
    </div>
  )
}
